import { useState } from 'react'

import CardState, { type CardStateValue } from '@/components/ui/CardState'
import SectionHead from '@/components/ui/SectionHead'
import Skeleton from '@/components/ui/Skeleton'
import { useMarketMood } from '@/features/market/api/queries'
import { MOOD_CUTS, moodBandByKey } from '@/features/market/lib/mood'
import MoodGauge from '@/features/market-rail/components/MoodGauge'
import { useIsMarketRoute } from '@/features/market-rail/marketRoute'
import { useRailBlock } from '@/features/market-rail/useRailFit'
import { cn } from '@/lib/utils'
import MoodExplainer from '@/screens/app/home/components/mood/MoodExplainer'
import { moodArt } from '@/screens/app/home/components/mood/moodArt'
import type { MarketMood } from '@/schemas/market'

/** What the reading and the track do while the reveal shows (mock `.greed-mood`): fade out. */
const AWAY =
  'transition-opacity duration-fast ease-out group-hover/mood:opacity-0 group-focus-visible/mood:opacity-0'

/**
 * Row-plus-track, shaped like the loaded body so the skeleton sits at
 * roughly the same place inside the reserved `h-14` box.
 */
function FearGreedSkeleton() {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <Skeleton className="h-5 w-9 rounded-xs" />
        <Skeleton className="h-4 w-16 rounded-xs" />
      </div>
      <Skeleton className="mt-3 h-2 w-full rounded-xs" />
    </div>
  )
}

/**
 * The loaded reading as the mock draws it (`greed-mood`): a button over the score, its mood and
 * the five-band track. Pointing at it (or focusing it) fades those out and brings up Mitra in the
 * mood's pose with "25 · Cautious" over the mood's line, rising 4px; pressing it opens the
 * explainer Home's mood cell opens, "Fear and greed · NEPSE".
 */
function MoodButton({ mood }: { mood: MarketMood }) {
  const [open, setOpen] = useState(false)
  const band = moodBandByKey(mood.band)
  const score = Math.round(mood.score)

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-label={`Market mood ${String(score)}, ${band.name}: ${band.line}. Open the explainer.`}
        onClick={() => {
          setOpen(true)
        }}
        className="group/mood relative block w-full cursor-pointer text-left outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
      >
        <span className={cn('flex items-baseline justify-between gap-3', AWAY)}>
          <span className="tnum text-h-section tracking-normal">{score}</span>
          <span className="text-body-m text-muted-foreground">{band.name}</span>
        </span>
        <MoodGauge
          score={mood.score}
          label={band.name}
          cuts={MOOD_CUTS}
          className={cn('mt-3', AWAY)}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex translate-y-1 items-center gap-2 opacity-0 transition-[opacity,translate] duration-med ease-out group-hover/mood:translate-y-0 group-hover/mood:opacity-100 group-focus-visible/mood:translate-y-0 group-focus-visible/mood:opacity-100"
        >
          <img
            src={moodArt(mood).src}
            alt=""
            width={36}
            height={44}
            className="h-11 w-9 shrink-0 object-contain"
          />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-h-s text-foreground">
              {score} · {band.name}
            </span>
            <span className="truncate text-body-xs text-muted-foreground">{band.line}</span>
          </span>
          <span className="shrink-0 text-h-m text-muted-foreground">›</span>
        </span>
      </button>
      <MoodExplainer mood={mood} open={open} onOpenChange={setOpen} />
    </>
  )
}

/**
 * The query, gated to mount only where `FearGreedCard` below has already confirmed the route, so
 * `useMarketMood`'s hook call is never conditional on anything.
 *
 * The reading is mm-be's Fear & Greed composite (`useMarketMood`, `/market/summary/`'s
 * `fear_greed`), the same mood Home's session strip and its explainer read: the app shows one
 * mood, not two. The Stock rail's reading is the company's own, a different model.
 *
 * States follow the `CardState` machine the other rail blocks use: `failed`, and `empty` for a
 * summary with no reading for the latest session, both say what happened in the reserved row.
 */
function FearGreedReading() {
  const block = useRailBlock('fear-greed')
  const { data, isPending, isError } = useMarketMood()

  const state: CardStateValue = isPending
    ? 'loading'
    : isError
      ? 'failed'
      : data === null
        ? 'empty'
        : 'loaded'

  return (
    <section aria-label="Fear and greed" {...block}>
      <SectionHead size="sm" title="Fear and greed" />
      {/* `h-14`: fixed, not a minimum, and the same box for all four states — to the rail's fit
          this block is one fixed cost, and a block that changed height after mount would re-price
          the other cards' rows once they had painted. */}
      <div className="mt-2 flex h-14 flex-col justify-center">
        <CardState
          state={state}
          size="compact"
          skeleton={<FearGreedSkeleton />}
          failedTitle="Couldn't load the reading"
          failedDescription="Try again shortly."
          emptyTitle="No reading yet"
          emptyDescription="It appears once the latest session is scored."
        >
          {data ? <MoodButton mood={data} /> : null}
        </CardState>
      </div>
    </section>
  )
}

/**
 * The market's fear-and-greed reading on `/market` and its tool pages (owner ask, 2026-09-29),
 * drawn between Market news and Top movers. Route-gated with the router's own state, so no screen
 * outside `/market/*` pays for its query.
 */
export default function FearGreedCard() {
  return useIsMarketRoute() ? <FearGreedReading /> : null
}
